import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportQueueService } from '../../../../application/ReportsQueueService';
import { STOP_STATUS } from '../../../../constants/Stops';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stop/Stop';
import { RouteActionType } from '../../route/context/actions';
import { useRouteContext } from '../../route/context/context';
import { getRouteMutationKey } from '../../route/hooks';
import { usePendingReports } from './usePendingReports';

interface UseDiscardReportDataProps {
  stopId: string;
  routeId: string;
}

type UseDiscardReportResponseProps = ServiceResponse<({ stopId, routeId }: UseDiscardReportDataProps) => void, void> & {
  reset: () => void;
};

// Hooks act as 'Adapter' layer.
export const useDiscardPendingReport = (): UseDiscardReportResponseProps => {
  const queryClient = useQueryClient();
  const { routeDispatch } = useRouteContext();
  const { refetch } = usePendingReports();

  const { isLoading, isSuccess, isError, mutate, reset } = useMutation<
    void,
    unknown,
    { stopId: string; routeId: string }
  >(
    async ({ stopId, routeId }: { stopId: string; routeId: string }): Promise<void> => {
      await reportQueueService.removeReport(stopId);
    },
    {
      onSuccess: (_data, variables) => {
        const routeMutationKey = getRouteMutationKey({ routeId: variables.routeId });

        const previousQueryContent = queryClient.getQueryData<Route>(routeMutationKey);
        if (!previousQueryContent) {
          throw new Error(`previousQueryContent does not exist`);
        }

        previousQueryContent.stops = previousQueryContent.getSortedStopList({}).map((s) => {
          if (s.id === variables.stopId) {
            return Stop.create(
              {
                ...s.props,
                status: STOP_STATUS.pending,
                report: undefined, // Discard the report
              },
              s.id
            );
          }
          return s;
        });

        queryClient.setQueryData<Route>(routeMutationKey, previousQueryContent);
        routeDispatch({ type: RouteActionType.FETCH_ROUTE_SUCCESS, payload: previousQueryContent });
        refetch();
      },
    }
  );

  const discardReport = ({ stopId, routeId }: UseDiscardReportDataProps) => {
    mutate({ stopId, routeId });
  };

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: discardReport,
    reset,
  };
};
